import { useEffect } from 'react';
import { PLATFORM_HOSTNAMES } from '../config/platformHostnames';

/**
 * SEO per le pagine pubbliche di una singola azienda (contatti, privacy,
 * "azienda non trovata"). Prima queste pagine non impostavano nulla: il
 * canonical restava quello della home di NoleggioinCloud (ereditato da
 * public/index.html) e il title quello generico della piattaforma.
 *
 * - title/description/og:title/og:description propri della pagina
 * - canonical e og:url = URL reale della pagina (dominio del cliente o
 *   www.noleggioincloud.app)
 * - noindex: aggiunge robots noindex e toglie canonical/og:url (pagina che
 *   non deve finire in indice, es. azienda inesistente = soft 404)
 * Al cambio pagina ripristina lo stato precedente.
 *
 * Non fa nulla finché `enabled` è false (dati ancora in caricamento).
 */
export function useCompanyPageSEO({ title, description, noindex = false, enabled = true }) {
  useEffect(() => {
    if (!enabled) return undefined;

    const restores = [];
    const setMeta = (selector, attr, attrValue, content) => {
      if (content == null) return;
      let el = document.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      restores.push(() => (created ? el.remove() : el.setAttribute('content', prev)));
    };

    const prevTitle = document.title;
    if (title) document.title = title;
    restores.push(() => { document.title = prevTitle; });

    if (title) {
      setMeta('meta[property="og:title"]', 'property', 'og:title', title);
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    }
    if (description) {
      setMeta('meta[name="description"]', 'name', 'description', description);
      setMeta('meta[property="og:description"]', 'property', 'og:description', description);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    const { hostname, origin, pathname } = window.location;
    const isPlatform = PLATFORM_HOSTNAMES.has(hostname) && hostname !== 'localhost' && hostname !== '127.0.0.1';
    const selfUrl = (isPlatform ? 'https://www.noleggioincloud.app' : origin) + pathname.replace(/\/+$/, '');

    const canonEl = document.querySelector('link[rel="canonical"]');
    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    if (noindex) {
      const robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex,follow');
      robots.setAttribute('data-nic', '1');
      document.head.appendChild(robots);
      restores.push(() => robots.remove());
      const prevCanon = canonEl && canonEl.getAttribute('href');
      const prevOg = ogUrlEl && ogUrlEl.getAttribute('content');
      if (canonEl) canonEl.removeAttribute('href');
      if (ogUrlEl) ogUrlEl.removeAttribute('content');
      restores.push(() => {
        if (canonEl && prevCanon) canonEl.setAttribute('href', prevCanon);
        if (ogUrlEl && prevOg) ogUrlEl.setAttribute('content', prevOg);
      });
    } else {
      const prevCanon = canonEl && canonEl.getAttribute('href');
      const prevOg = ogUrlEl && ogUrlEl.getAttribute('content');
      if (canonEl) canonEl.setAttribute('href', selfUrl);
      if (ogUrlEl) ogUrlEl.setAttribute('content', selfUrl);
      restores.push(() => {
        if (canonEl && prevCanon) canonEl.setAttribute('href', prevCanon);
        if (ogUrlEl && prevOg) ogUrlEl.setAttribute('content', prevOg);
      });
    }

    return () => restores.reverse().forEach(fn => fn());
  }, [title, description, noindex, enabled]);
}
